<template>
  <div>
    <b-container fluid>
      <b-row>
        <b-col>
          <h2>Integrações MCP</h2>
          <p class="text-muted">
            Tokens de acesso pessoal para conectar clientes de IA (Claude Code, ChatGPT, Claude web) ao servidor MCP da Easytick.
            Cada token vale como uma senha e concede acesso administrativo às ferramentas operacionais.
          </p>
        </b-col>
      </b-row>

      <b-row class="mb-3">
        <b-col>
          <b-button variant="success" @click="openCreateModal">
            <icon name="plus" scale="0.9"></icon> Gerar token
          </b-button>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12" style="overflow-x: auto;">
          <b-table striped hover show-empty
            :items="tokens"
            :fields="fields"
            primary-key="id"
            :busy="loading"
            empty-text="Nenhum token gerado ainda"
          >
            <template v-slot:table-busy>
              <div class="text-center my-3">
                <b-spinner class="align-middle"></b-spinner>
              </div>
            </template>

            <template v-slot:cell(name)="data">
              <span :class="{ 'text-muted': data.item.status !== 'active' }">{{ data.item.name }}</span>
            </template>

            <template v-slot:cell(last_used_at)="data">
              <span v-if="data.item.last_used_at">{{ data.item.last_used_at | date }}</span>
              <span v-else class="text-muted">nunca</span>
            </template>

            <template v-slot:cell(expires_at)="data">
              <span v-if="data.item.expires_at">{{ data.item.expires_at | date }}</span>
              <span v-else class="text-muted">sem expiração</span>
            </template>

            <template v-slot:cell(status)="data">
              <b-badge :variant="statusVariant(data.item.status)">{{ statusLabel(data.item.status) }}</b-badge>
            </template>

            <template v-slot:cell(actions)="data">
              <b-button
                size="sm"
                variant="danger"
                :disabled="data.item.status === 'revoked'"
                @click="revokeToken(data.item)"
                v-b-tooltip.hover
                title="Revogar"
              >
                <icon name="trash" scale="0.9"></icon>
              </b-button>
            </template>
          </b-table>
        </b-col>
      </b-row>
    </b-container>

    <!-- Create modal -->
    <b-modal
      id="mcp-create-modal"
      title="Gerar token de acesso MCP"
      :ok-disabled="creating || !newToken.name"
      ok-title="Gerar"
      cancel-title="Cancelar"
      ok-variant="success"
      @ok.prevent="createToken"
    >
      <b-form-group label="Nome / identificação" label-for="mcp-token-name">
        <b-form-input
          id="mcp-token-name"
          v-model="newToken.name"
          maxlength="100"
          placeholder="Ex.: Claude Code — notebook Túlio"
          required
        ></b-form-input>
      </b-form-group>

      <b-form-group label="Validade">
        <b-form-select v-model="newToken.expires_in_days" :options="validityOptions"></b-form-select>
      </b-form-group>
    </b-modal>

    <!-- One-time result modal -->
    <b-modal
      id="mcp-result-modal"
      title="Token gerado"
      ok-only
      ok-title="Fechar"
      no-close-on-backdrop
      no-close-on-esc
      size="lg"
    >
      <b-alert show variant="warning">
        <strong>Guarde agora.</strong> Este valor não será exibido novamente. Trate o token e a URL como uma senha.
      </b-alert>

      <b-form-group label="Token (Authorization: Bearer)">
        <b-input-group>
          <b-form-input :value="createdToken.token" readonly></b-form-input>
          <b-input-group-append>
            <b-button @click="copy(createdToken.token)"><icon name="copy" scale="0.9"></icon></b-button>
          </b-input-group-append>
        </b-input-group>
      </b-form-group>

      <b-form-group label="URL de conexão (clientes web sem autenticação)">
        <b-input-group>
          <b-form-input :value="connectUrl" readonly></b-form-input>
          <b-input-group-append>
            <b-button @click="copy(connectUrl)"><icon name="copy" scale="0.9"></icon></b-button>
          </b-input-group-append>
        </b-input-group>
        <small class="text-muted">Trate esta URL como uma senha — quem a tiver terá o mesmo acesso do token.</small>
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
export default {
  name: 'AdminMcpTokens',
  data () {
    return {
      tokens: [],
      loading: false,
      creating: false,
      newToken: {
        name: '',
        expires_in_days: null
      },
      createdToken: {
        token: '',
        name: ''
      },
      validityOptions: [
        { value: null, text: 'Sem expiração' },
        { value: 30, text: '30 dias' },
        { value: 90, text: '90 dias' },
        { value: 365, text: '365 dias' }
      ],
      fields: [
        { key: 'name', label: 'Nome', class: ['align-middle'] },
        { key: 'created_by', label: 'Criado por', class: ['align-middle'] },
        { key: 'created_at', label: 'Criado em', class: ['align-middle', 'text-nowrap'], formatter: this.$options.filters.date },
        { key: 'last_used_at', label: 'Último uso', class: ['align-middle', 'text-nowrap'] },
        { key: 'expires_at', label: 'Expira em', class: ['align-middle', 'text-nowrap'] },
        { key: 'status', label: 'Status', class: ['align-middle'] },
        { key: 'actions', label: 'Ações', class: ['text-center', 'align-middle'] }
      ]
    }
  },
  computed: {
    connectUrl () {
      if (!this.createdToken.token) {
        return ''
      }
      return `${process.env.VUE_APP_URL_APP}/b/mcp/connect/${this.createdToken.token}`
    }
  },
  async mounted () {
    await this.loadTokens()
  },
  methods: {
    statusLabel (status) {
      return { active: 'Ativo', expired: 'Expirado', revoked: 'Revogado' }[status] || status
    },
    statusVariant (status) {
      return { active: 'success', expired: 'secondary', revoked: 'dark' }[status] || 'secondary'
    },
    openCreateModal () {
      this.newToken = { name: '', expires_in_days: null }
      this.$bvModal.show('mcp-create-modal')
    },
    async loadTokens () {
      this.loading = true
      try {
        const resp = await this.$http.get(process.env.VUE_APP_URL_APP + '/b/admin/mcp-tokens')
        this.tokens = (resp.body && resp.body.tokens) || []
      } catch (e) {
        console.error(e)
        const msg = (e.body && e.body.message) || 'Erro ao carregar os tokens MCP'
        this.$bvToast.toast(msg, { title: 'Erro', variant: 'danger', solid: true })
      } finally {
        this.loading = false
      }
    },
    async createToken () {
      if (this.creating || !this.newToken.name) {
        return
      }
      this.creating = true
      try {
        const payload = { name: this.newToken.name }
        if (this.newToken.expires_in_days) {
          payload.expires_in_days = this.newToken.expires_in_days
        }
        const resp = await this.$http.post(process.env.VUE_APP_URL_APP + '/b/admin/mcp-tokens', payload)

        this.createdToken = resp.body
        this.$bvModal.hide('mcp-create-modal')
        this.$bvModal.show('mcp-result-modal')
        await this.loadTokens()
      } catch (e) {
        console.error(e)
        const msg = (e.body && e.body.message) || 'Erro ao gerar o token MCP'
        this.$bvToast.toast(msg, { title: 'Erro', variant: 'danger', solid: true })
      } finally {
        this.creating = false
      }
    },
    async revokeToken (row) {
      const action = await this.$bvModal.msgBoxConfirm(
        `Revogar o token "${row.name}"? Clientes que o utilizam perdem o acesso na próxima requisição. Esta ação é irreversível.`, {
          title: '⚠ Atenção',
          okVariant: 'danger',
          cancelVariant: 'secondary',
          okTitle: 'Revogar',
          cancelTitle: 'Cancelar',
          centered: true,
          hideHeaderClose: false
        })

      if (action === false || !action) {
        return
      }

      try {
        await this.$http.delete(process.env.VUE_APP_URL_APP + `/b/admin/mcp-tokens/${row.id}`)
        this.$bvToast.toast('Token revogado', { title: 'Sucesso', variant: 'success', solid: true })
        await this.loadTokens()
      } catch (e) {
        console.error(e)
        const msg = (e.body && e.body.message) || 'Erro ao revogar o token MCP'
        this.$bvToast.toast(msg, { title: 'Erro', variant: 'danger', solid: true })
      }
    },
    copy (text) {
      if (!text) {
        return
      }
      const done = () => this.$bvToast.toast('Copiado', { title: 'Sucesso', variant: 'success', solid: true })
      if (navigator.clipboard) {
        navigator.clipboard.writeText(text).then(done).catch(() => this.fallbackCopy(text, done))
      } else {
        this.fallbackCopy(text, done)
      }
    },
    fallbackCopy (text, done) {
      const el = document.createElement('textarea')
      el.value = text
      document.body.appendChild(el)
      el.select()
      document.execCommand('copy')
      document.body.removeChild(el)
      done()
    }
  }
}
</script>
